/**
 * ============================================
 * COUNTRY MULTI-SELECT (checkbox dropdown)
 * ============================================
 *
 * "All" or any set of countries, grouped by region with a per-region
 * select-all, plus search. Shared by the Analytics, Multiplayer and Sony/DOMO
 * tabs; each tab keeps its own selection. `selected = []` means All.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Globe, Search, X } from 'lucide-react';
import { REGION_INFO, groupCountryOptions, type CountryOption } from '@/utils/countryRegions';

interface Props {
  options: CountryOption[];
  selected: string[];
  onChange: (codes: string[]) => void;
  isDark: boolean;
  /** what the count next to each country means, e.g. "players" */
  countLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
  /** which edge of the button the panel lines up with */
  align?: 'left' | 'right';
  /** small button for card headers */
  compact?: boolean;
  /** card mode: the card currently follows the tab's filter (`selected` = the tab's selection) */
  following?: boolean;
  /** card mode: go back to following the tab's filter */
  onFollow?: () => void;
}

function Checkbox({ checked, indeterminate, onChange, isDark }: {
  checked: boolean; indeterminate?: boolean; onChange: () => void; isDark: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input ref={ref} type="checkbox" checked={checked} onChange={onChange}
      className={`h-3.5 w-3.5 rounded accent-blue-600 cursor-pointer ${isDark ? 'bg-gray-700' : ''}`} />
  );
}

export function CountryMultiSelect({
  options, selected, onChange, isDark, countLabel = 'players', disabled, disabledReason, align = 'right',
  compact = false, following = false, onFollow,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Region groups start collapsed; a search shows every match regardless.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (region: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(region)) next.delete(region); else next.add(region);
    return next;
  });
  // "Nothing ticked" while picking. The parent still sees [] (= All) so the page
  // never goes empty; it becomes a real selection with the first tick, and
  // reverts to All if the panel is closed with nothing ticked.
  const [none, setNone] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = () => { setOpen(false); setNone(false); };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const allCodes = useMemo(() => options.map(o => o.code), [options]);
  const total = useMemo(() => options.reduce((s, o) => s + o.count, 0), [options]);
  const isAll = selected.length === 0 && !none;
  const selectedSet = useMemo(
    () => new Set(none && !selected.length ? [] : selected.length ? selected : allCodes),
    [none, allCodes, selected]
  );

  const groups = useMemo(() => groupCountryOptions(options, query), [options, query]);

  // Normalise: every country ticked == All ([]); nothing ticked == "none" state.
  const commit = (next: Set<string>) => {
    const codes = allCodes.filter(c => next.has(c));
    if (!codes.length) {
      setNone(true);
      onChange([]);
      return;
    }
    setNone(false);
    onChange(codes.length === allCodes.length ? [] : codes);
  };
  const selectAll = () => { setNone(false); onChange([]); };
  const only = (codes: string[]) => { setNone(false); onChange(codes); };
  const toggle = (codes: string[], on: boolean) => {
    const next = new Set(selectedSet);
    codes.forEach(c => (on ? next.add(c) : next.delete(c)));
    commit(next);
  };

  const baseLabel = none && !selected.length
    ? 'Select countries…'
    : isAll
    ? (compact ? 'All countries' : `All countries (${total.toLocaleString()})`)
    : selected.length === 1
      ? options.find(o => o.code === selected[0])?.name ?? selected[0]
      : `${selected.length} of ${allCodes.length} countries`;
  // Card mode: "Tab · …" while following a narrowed tab filter.
  const label = !following || none ? baseLabel : isAll ? 'Country/Region' : `Tab · ${baseLabel}`;
  const highlighted = following ? false : onFollow ? true : !isAll;

  const btnCls = isDark
    ? 'bg-gray-800 border-gray-700 text-gray-100 hover:bg-gray-700'
    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50';
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="relative" ref={rootRef}>
      <button type="button" disabled={disabled}
        title={disabled ? disabledReason : onFollow ? (following ? 'Countries for this card (following the tab filter)' : 'Countries for this card only') : 'Filter by country'}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex items-center rounded-lg border font-medium disabled:opacity-50 disabled:cursor-not-allowed ${btnCls} ${
          compact ? 'gap-1 px-2 py-0.5 text-[11px]' : 'gap-2 px-3 py-2 text-sm'} ${
          highlighted ? 'ring-1 ring-blue-500' : ''} ${following ? (isDark ? 'text-gray-400' : 'text-gray-500') : ''}`}>
        <Globe className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-blue-400`} />
        <span className={`${compact ? 'max-w-[9rem]' : 'max-w-[14rem]'} truncate`}>{label}</span>
        {(onFollow ? !following : !isAll) && (
          <span role="button" aria-label={onFollow ? 'Follow the tab filter' : 'Clear country filter'}
            title={onFollow ? 'Follow the tab filter again' : 'Show all countries'}
            onClick={e => { e.stopPropagation(); if (onFollow) { setNone(false); onFollow(); } else selectAll(); }}
            className={`rounded p-0.5 ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
            <X className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </span>
        )}
        <ChevronDown className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} ${muted}`} />
      </button>

      {open && (
        <div className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} z-50 mt-1 w-80 rounded-xl border shadow-xl ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="relative">
              <Search className={`absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${muted}`} />
              <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search countries or SIEA / SIEE / SIEAsia / SIEJ"
                className={`w-full rounded-md pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-900'}`} />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto py-1 text-xs">
            {onFollow && !query && (
              <label className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer font-semibold border-b ${
                isDark ? 'hover:bg-gray-800 border-gray-700' : 'hover:bg-gray-50 border-gray-200'}`}>
                <input type="radio" checked={following} readOnly onClick={() => { setNone(false); onFollow(); }}
                  className="h-3.5 w-3.5 accent-blue-600 cursor-pointer" />
                <span className="flex-1">Follow the tab filter</span>
              </label>
            )}
            {!query && (
              <label className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer font-semibold ${
                isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                <Checkbox checked={isAll} indeterminate={!isAll && selectedSet.size > 0} isDark={isDark}
                  onChange={() => (isAll ? (setNone(true), onChange([])) : selectAll())} />
                <span className="flex-1">All countries</span>
                <span className={muted}>{total.toLocaleString()}</span>
              </label>
            )}
            {groups.map(g => {
              const codes = g.options.map(o => o.code);
              const on = codes.filter(c => selectedSet.has(c)).length;
              const open = expanded.has(g.region) || !!query.trim();
              return (
                <div key={g.region} className="mt-1">
                  <label title={REGION_INFO[g.region]} className={`group flex items-center gap-2 pl-1.5 pr-3 py-1.5 cursor-pointer font-semibold tracking-wide text-[11px] ${
                    isDark ? 'text-gray-200 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                    <button type="button" aria-label={open ? `Collapse ${g.region}` : `Expand ${g.region}`} aria-expanded={open}
                      onClick={e => { e.preventDefault(); toggleExpanded(g.region); }}
                      className={`rounded p-0.5 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
                      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                    <Checkbox checked={on === codes.length} indeterminate={on > 0} isDark={isDark}
                      onChange={() => toggle(codes, on !== codes.length)} />
                    <span className="flex-1 truncate" onClick={e => { e.preventDefault(); toggleExpanded(g.region); }}>{g.region}
                      <span className={`ml-1.5 font-normal ${muted}`}>
                        {on === codes.length || on === 0 ? `${codes.length} ${codes.length === 1 ? 'country' : 'countries'}` : `${on} of ${codes.length}`}
                      </span>
                    </span>
                    <button type="button" onClick={e => { e.preventDefault(); only(codes); }}
                      className="hidden group-hover:inline text-[10px] font-normal normal-case text-blue-500 hover:underline">only</button>
                    <span className={`normal-case font-normal ${muted}`}>{g.count.toLocaleString()}</span>
                  </label>
                  {open && g.options.map(o => (
                    <label key={o.code} className={`group flex items-center gap-2 pl-8 pr-3 py-1 cursor-pointer ${
                      isDark ? 'text-gray-200 hover:bg-gray-800' : 'text-gray-800 hover:bg-gray-50'}`}>
                      <Checkbox checked={selectedSet.has(o.code)} isDark={isDark}
                        onChange={() => toggle([o.code], !selectedSet.has(o.code))} />
                      <span className="flex-1 truncate">{o.name}</span>
                      <button type="button" onClick={e => { e.preventDefault(); only([o.code]); }}
                      className="hidden group-hover:inline text-[10px] font-normal normal-case text-blue-500 hover:underline">only</button>
                      <span className={muted}>{o.count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              );
            })}
            {!groups.length && <div className={`px-3 py-4 text-center ${muted}`}>No country matches “{query}”</div>}
          </div>
          <div className={`flex items-center justify-between px-3 py-2 border-t text-[11px] ${
            isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            <span className="flex gap-2">
              <span>Counts are {countLabel}</span>
              <button type="button" className="text-blue-500 hover:underline"
                onClick={() => setExpanded(expanded.size ? new Set() : new Set(groups.map(g => g.region)))}>
                {expanded.size ? 'Collapse' : 'Expand'}
              </button>
            </span>
            <span className="flex gap-3">
              <button type="button" className="text-blue-500 hover:underline" onClick={() => { setNone(true); onChange([]); }}>Clear all</button>
              <button type="button" className="text-blue-500 hover:underline" onClick={selectAll}>Select all</button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
